import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  listDoorsForSchool,
  createDoor,
  deleteDoor,
} from '../../api/hikvision'
import { lastPollLine } from '../../lib/lastPollLabel'
import { isCardFieldUnique, sharedText } from '../../lib/schoolEquipmentIdentity'
import AddHikvisionDeviceModal from '../AddHikvisionDeviceModal'
import {
  AddDeviceLink,
  availabilityAccent,
  DeviceRowCard,
  EquipmentEmpty,
  IconAction,
  SectionFrame,
  StatusDot,
} from './schoolEquipmentChrome'

const isAdminOrEngineer = (role) => {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : ''
  return r === 'admin' || r === 'engineer'
}

const pluralizeTurnstiles = (count) => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return `${count} турникет`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} турникета`
  return `${count} турникетов`
}

const connectivity = (door) => {
  if (door.enabled === false) return { tone: 'muted', label: 'Опрос выключен' }
  if (door.status === true) return { tone: 'ok', label: 'Доступен' }
  if (door.status === false) return { tone: 'critical', label: 'Недоступен' }
  return { tone: 'muted', label: 'Нет данных' }
}

const HikvisionDoorsSection = ({ objectIp, userRole }) => {
  const navigate = useNavigate()
  const canManage = isAdminOrEngineer(userRole)

  const [doors, setDoors] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  const fetchDoors = async () => {
    if (!objectIp) return
    setIsLoading(true)
    try {
      const data = await listDoorsForSchool(objectIp)
      setDoors(data)
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Ошибка загрузки')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchDoors()
    const interval = setInterval(fetchDoors, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [objectIp])

  const handleCreate = async (data) => {
    setIsCreating(true)
    setCreateError(null)
    try {
      await createDoor(objectIp, data)
      setIsAddModalOpen(false)
      fetchDoors()
    } catch (err) {
      setCreateError(err?.response?.data?.message || err?.message || 'Ошибка создания')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить это устройство?')) return
    setDeletingId(id)
    try {
      await deleteDoor(id)
      fetchDoors()
    } catch (err) {
      alert(err?.response?.data?.message || 'Ошибка удаления')
    } finally {
      setDeletingId(null)
    }
  }

  const doorDevices = useMemo(
    () => doors.filter((d) => d.device_type === 'door' || !d.device_type),
    [doors]
  )

  const availableCount = useMemo(
    () => doorDevices.filter((d) => d.enabled !== false && d.status === true).length,
    [doorDevices]
  )

  const sharedModel = sharedText(doorDevices, 'model')
  const sharedFirmware = sharedText(doorDevices, 'firmware')

  const subtitle = useMemo(() => {
    if (doorDevices.length === 0) return 'Нет турникетов'
    const availableLabel = availableCount === 1 ? '1 доступен' : `${availableCount} доступны`
    const parts = [pluralizeTurnstiles(doorDevices.length), availableLabel]
    if (sharedModel) parts.push(sharedModel)
    if (sharedFirmware) parts.push(sharedFirmware)
    return parts.join(' · ')
  }, [doorDevices.length, availableCount, sharedModel, sharedFirmware])

  return (
    <SectionFrame
      eyebrow="Контроль доступа"
      title="Турникеты"
      subtitle={subtitle}
      actions={
        canManage ? <AddDeviceLink onClick={() => setIsAddModalOpen(true)} /> : null
      }
    >
          {isLoading && (
            <div className="flex h-24 items-center justify-center text-sm text-muted-300">
          <span className="material-icons mr-2 animate-spin text-brand-300">progress_activity</span>
          Загрузка устройств...
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-2xl bg-critical/10 px-5 py-4 text-sm text-critical">{error}</div>
      )}

      {!isLoading && !error && doorDevices.length === 0 && (
        <EquipmentEmpty
          hint="Добавьте устройство, чтобы видеть статус дверей и события доступа."
          canManage={canManage}
          onAdd={() => setIsAddModalOpen(true)}
        />
      )}

      {!isLoading && !error && doorDevices.length > 0 && (
        <div className="flex flex-col gap-2">
          {doorDevices.map((door) => {
            const state = connectivity(door)
            const poll = door.status === false ? lastPollLine(door.scrapedAt) : null
            const extra = [
              isCardFieldUnique(doorDevices, 'model', door.model) ? door.model : null,
              isCardFieldUnique(doorDevices, 'firmware', door.firmware) ? door.firmware : null,
              poll,
            ]
              .filter(Boolean)
              .join(' · ') || null

            return (
              <DeviceRowCard
                key={door.id}
                icon="meeting_room"
                name={door.name || `#${door.id}`}
                address={door.target}
                extra={extra}
                status={<StatusDot tone={state.tone} label={state.label} />}
                accent={availabilityAccent(state.tone)}
                onOpen={() => navigate(`/schools/${encodeURIComponent(objectIp)}/doors/${door.id}`)}
                hoverActions={
                  canManage ? (
                    <IconAction
                      title="Удалить устройство"
                      onClick={() => handleDelete(door.id)}
                      busy={deletingId === door.id}
                      icon="delete"
                      tone="critical"
                    />
                  ) : null
                }
              />
            )
          })}
        </div>
      )}

      <AddHikvisionDeviceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreate}
        isLoading={isCreating}
        error={createError}
      />
    </SectionFrame>
  )
}

export default HikvisionDoorsSection
